import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import DashNavbar from "../components/PlumPlusDash/DashNavbar.jsx";

// -----------------------------------------------------------------------------
// Utility & Constants
// -----------------------------------------------------------------------------

/** parseMonths: extrait un nombre de mois d'une chaîne ("1-2" => 1.5) */
function parseMonths(str) {
  if (!str) return 0;
  const interval = str.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (interval) {
    return (
      (parseFloat(interval[1]) + parseFloat(interval[2])) / 2
    );
  }
  const single = str.match(/(\d+(?:\.\d+)?)/);
  return single ? parseFloat(single[1]) : 0;
}

// Durées par défaut (en mois)
const DEFAULT_DOCS_MIN = 0.5;
const DEFAULT_DOCS_MAX = 1;

// Liste des pays convention de La Haye
const hagueCountries = [
  { code: "AL", name: "Albanie" },
  { code: "DE", name: "Allemagne" },
  { code: "AD", name: "Andorre" },
  { code: "AG", name: "Antigua-et-Barbuda" },
  { code: "AR", name: "Argentine" },
  { code: "AM", name: "Arménie" },
  { code: "AU", name: "Australie" },
  { code: "AT", name: "Autriche" },
  { code: "AZ", name: "Azerbaïdjan" },
  { code: "BS", name: "Bahamas" },
  { code: "BB", name: "Barbade" },
  { code: "BY", name: "Bélarus" },
  { code: "BE", name: "Belgique" },
  { code: "BZ", name: "Belize" },
  { code: "BA", name: "Bosnie-Herzégovine" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brésil" },
  { code: "BG", name: "Bulgarie" },
  { code: "CA", name: "Canada" },
  { code: "CL", name: "Chili" },
  { code: "CN", name: "Chine" }, // Note: Applies to Hong Kong SAR and Macao SAR as well
  { code: "CY", name: "Chypre" },
  { code: "CO", name: "Colombie" },
  { code: "CR", name: "Costa Rica" },
  { code: "HR", name: "Croatie" },
  { code: "DK", name: "Danemark" },
  { code: "DO", name: "République dominicaine" },
  { code: "EG", name: "Égypte" },
  { code: "SV", name: "El Salvador" },
  { code: "EE", name: "Estonie" },
  { code: "US", name: "États-Unis d'Amérique" },
  { code: "FI", name: "Finlande" },
  { code: "FR", name: "France" },
  { code: "GE", name: "Géorgie" },
  { code: "GR", name: "Grèce" },
  { code: "HU", name: "Hongrie" },
  { code: "IN", name: "Inde" },
  { code: "IE", name: "Irlande" },
  { code: "IS", name: "Islande" },
  { code: "IL", name: "Israël" },
  { code: "IT", name: "Italie" },
  { code: "JP", name: "Japon" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KW", name: "Koweït" },
  { code: "LV", name: "Lettonie" },
  { code: "LT", name: "Lituanie" },
  { code: "LU", name: "Luxembourg" },
  { code: "MW", name: "Malawi" },
  { code: "MT", name: "Malte" },
  { code: "MH", name: "Îles Marshall" },
  { code: "MX", name: "Mexique" },
  { code: "MC", name: "Monaco" },
  { code: "ME", name: "Monténégro" },
  { code: "MA", name: "Maroc" },
  { code: "NL", name: "Pays-Bas" },
  { code: "NI", name: "Nicaragua" },
  { code: "MK", name: "Macédoine du Nord" },
  { code: "NO", name: "Norvège" },
  { code: "PK", name: "Pakistan" },
  { code: "PY", name: "Paraguay" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Pologne" },
  { code: "PT", name: "Portugal" },
  { code: "KR", name: "République de Corée" },
  { code: "MD", name: "République de Moldova" },
  { code: "RO", name: "Roumanie" },
  { code: "GB", name: "Royaume-Uni" },
  { code: "RU", name: "Fédération de Russie" },
  { code: "VC", name: "Saint-Vincent-et-les Grenadines" },
  { code: "SM", name: "Saint-Marin" },
  { code: "RS", name: "Serbie" },
  { code: "SC", name: "Seychelles" },
  { code: "SG", name: "Singapour" },
  { code: "SK", name: "Slovaquie" },
  { code: "SI", name: "Slovénie" },
  { code: "ES", name: "Espagne" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SE", name: "Suède" },
  { code: "CH", name: "Suisse" },
  { code: "TN", name: "Tunisie" },
  { code: "TR", name: "Türkiye" },
  { code: "UA", name: "Ukraine" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Viet Nam" },
].map((c) => ({ ...c, isSignatory: true }));

const nonHagueCountries = [
  // { code: "BR", name: "Brésil" }, // Now in hagueCountries
  // { code: "CN", name: "Chine" }, // Now in hagueCountries
  // { code: "IN", name: "Inde" }, // Now in hagueCountries
  { code: "DZ", name: "Algérie" },
].map((c) => ({ ...c, isSignatory: false }));

const allCountries = [...hagueCountries, ...nonHagueCountries].sort((a, b) =>
  a.name.localeCompare(b.name)
);

// ────────────────────────────────────────────────────────────────────────────
// 2) PHASE 1 : Préparation
// ────────────────────────────────────────────────────────────────────────────

const preparationSteps = [
  {
    id: "intro",
    phase: "preparation",
    title: "Introduction & contexte",
    shortContent: "Vue d'ensemble de la garde d'enfants pour conjoints de fait.",
    detailedContent: `
      Cette frise interactive vous guide dans les étapes relatives 
      à la garde d'enfants et à la pension alimentaire pour les conjoints de fait au Québec.
      Elle inclut la phase de préparation, la procédure judiciaire (si contestation) 
      et des informations post-séparation.
    `,
    minDuration: 0,
    maxDuration: 0,
    questions: [],
  },
  {
    id: "cadre_legal",
    phase: "preparation",
    title: "Cadre légal des conjoints de fait",
    shortContent: "Comprendre vos droits et obligations en tant que parents.",
    detailedContent: `
      Même sans être mariés, vous avez des obligations envers vos enfants.
      Le tribunal peut fixer la garde et la pension alimentaire.
      L'intérêt de l'enfant prime toujours dans les décisions.
    `,
    minDuration: 0.25,
    maxDuration: 0.5,
    questions: [
      {
        id: "q_cadre_compris",
        label: "Avez-vous compris vos obligations parentales ?",
        options: [
          { value: "oui", label: "Oui, je comprends mes responsabilités" },
          { value: "non", label: "Non, j'ai besoin de plus d'informations" },
        ],
      },
    ],
  },
  {
    id: "documents",
    phase: "preparation",
    title: "Rassemblement des documents",
    shortContent: "Certificats de naissance, preuves de revenus, etc.",
    detailedContent: `
      Vous aurez besoin des certificats de naissance des enfants, 
      de vos preuves de revenus (bulletins de paie, avis de cotisation), 
      et éventuellement d'autres documents (médicaux, scolaires) selon la situation.
      Préparez tout ce qui pourrait être nécessaire pour évaluer la pension alimentaire 
      ou déterminer la garde (horaires de travail, etc.).
    `,
    minDuration: DEFAULT_DOCS_MIN,
    maxDuration: DEFAULT_DOCS_MAX,
    questions: [
      {
        id: "q_docs_delay",
        label: "Durée prévue pour réunir les documents (ex: '1' ou '2-3') :",
        type: "text",
        help: "Laissez vide pour le délai par défaut (0.5 à 1 mois).",
      },
    ],
  },
  {
    id: "mediation_familiale",
    phase: "preparation",
    title: "Médiation familiale",
    shortContent: "Souvent recommandée (ou obligatoire) avant la Cour.",
    detailedContent: `
      Le Québec encourage la médiation familiale afin d'éviter un litige long et coûteux. 
      Le médiateur peut vous aider à établir un plan de garde et une pension alimentaire. 
      Les premières heures sont souvent subventionnées.
    `,
    minDuration: 1,
    maxDuration: 2,
    questions: [
      {
        id: "q_mediation",
        label: "Avez-vous déjà entamé la médiation ?",
        options: [
          { value: "oui", label: "Oui, c'est déjà en cours ou terminé" },
          { value: "non", label: "Non, pas encore" },
        ],
      },
    ],
  },
  {
    id: "district_judiciaire",
    phase: "preparation",
    title: "District judiciaire",
    shortContent: "Choisissez le district judiciaire (Montréal peut être plus lent).",
    detailedContent: `
      Au stade de la préparation, il est important de déterminer le district judiciaire
      où vous déposerez votre demande. À Montréal, par exemple, l'affluence est plus forte
      et peut engorger les délais.
    `,
    minDuration: 0,
    maxDuration: 0,
    questions: [
      {
        id: "q_district",
        label: "Quel est votre district judiciaire ?",
        options: [
          { value: "montreal", label: "Montréal (souvent plus chargé)" },
          { value: "autre", label: "Autre district" },
        ],
      },
    ],
  },
];

// ────────────────────────────────────────────────────────────────────────────
// 3) FONCTION DÉDIÉE POUR CALCULER LES DÉLAIS DE SIGNIFICATION (Phase 2 avancée)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Calque la logique du DivorceTimeline: 
 * - si mesures urgentes => signification réduite
 * - si adresse connue, en QC, hors QC, étranger, pays signataire/non...
 * - mode spécial électronique si adresse inconnue
 */
function calculateSignificationDelay(answers) {
  const stepSignif = answers["step_signification"] || {};
  const stepProv = answers["step_provisoires"] || {}; // si vous gérez la question "mesures provisoires"

  // 1) Si mesures provisoires urgentes => signification accélérée
  if (stepProv.mesures_provisoires === "oui") {
    return {
      min: 0.1, // ~3 jours
      max: 0.3, // ~10 jours
      msg: "[Signification] Urgente (mesures provisoires): 3-10 jours",
    };
  }

  // 2) Adresse connue
  if (stepSignif.adresse_exacte === "oui" || stepSignif.contacts_recents === "oui") {
    switch (stepSignif.localisation) {
      case "qc":
        return {
          min: 0.25,
          max: 0.5,
          msg: "[Signification] Québec - standard: 1-2 semaines",
        };
      case "canada":
        return {
          min: 0.25,
          max: 0.75,
          msg: "[Signification] Autre province du Canada: 1-3 semaines",
        };
      case "etranger": {
        const country = allCountries.find((c) => c.code === stepSignif.pays_etranger);
        if (country?.isSignatory) {
          return {
            min: 0.5,
            max: 2,
            msg: `[Signification] ${country.name} (Convention La Haye): 0.5-2 mois`,
          };
        }
        return {
          min: 1,
          max: 6,
          msg: `[Signification] ${country ? country.name : "Pays inconnu"} (Hors Convention): 1-6 mois`,
        };
      }
      default:
        return { min: 0.5, max: 1, msg: "[Signification] Québec (défaut)" };
    }
  }

  // 3) Mode spécial si contacts_electroniques ou publication
  if (
    stepSignif.contacts_electroniques === "oui" ||
    stepSignif.residence_canada === "oui"
  ) {
    if (stepSignif.mode_special_decision === "accepte") {
      return {
        min: 0.25,
        max: 0.5,
        msg: "[Signification] Mode spécial autorisé: 1-2 semaines",
      };
    }
    if (stepSignif.mode_special_decision === "refuse") {
      return {
        min: 0.5,
        max: 1,
        msg: "[Signification] Mode spécial refusé => Délai additionnel (2-4 semaines)",
      };
    }
    return {
      min: 0.5,
      max: 1,
      msg: "[Signification] En attente d'autorisation pour mode spécial",
    };
  }

  // 4) Cas vraiment inconnu => consultation
  return {
    min: 2,
    max: 4,
    msg: "[Signification] Adresse inconnue / Complexe => consultez un avocat",
  };
}

// ────────────────────────────────────────────────────────────────────────────
// 4) PHASE 2 : Processus judiciaire (logique avancée type DivorceTimeline)
// ────────────────────────────────────────────────────────────────────────────

const judiciaireSteps = [
  {
    id: "step_depot_demande",
    phase: "judiciaire",
    title: "Dépôt de la demande",
    shortContent: "Préparation et dépôt de la demande de garde/pension.",
    detailedContent: `
      Votre demande doit préciser le type de garde souhaité (partagée/exclusive),
      la pension alimentaire proposée, et tout autre arrangement important.
    `,
    minDuration: 0.5,
    maxDuration: 1,
    questions: [
      {
        id: "type_demande",
        label: "Quel type de garde demandez-vous ?",
        options: [
          { value: "partagee", label: "Garde partagée" },
          { value: "exclusive", label: "Garde exclusive" },
          { value: "droits", label: "Droits d'accès" },
        ],
      },
    ],
  },

  // 2) Signification (avec la logique avancée)
  {
    id: "step_signification",
    phase: "judiciaire",
    title: "Signification de la demande",
    minDuration: 0.25,
    maxDuration: 6,
    shortContent:
      "Faites signifier officiellement la requête à l'autre parent (délais variables).",
    detailedContent: `
      <p>
      La signification dépend du lieu de résidence de l'autre parent : 
      <ul class="list-disc list-inside">
        <li>Au Québec : ~1-2 semaines</li>
        <li>Ailleurs au Canada : 1-3 semaines</li>
        <li>À l'étranger : 0.5 à 6 mois selon la Convention de La Haye</li>
      </ul>
      Si l'adresse est inconnue, vous devrez demander un mode spécial 
      ou effectuer un dépistage/publication.
      </p>
    `,
    questions: [
      {
        id: "adresse_exacte",
        label: "Connaissez-vous l'adresse de l'autre parent ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
      {
        id: "contacts_recents",
        label: "Pouvez-vous l'obtenir via famille, proches, employeur ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) => answers["step_signification"]?.adresse_exacte === "non",
      },
      {
        id: "localisation",
        label: "Où réside l'autre parent ?",
        options: [
          { value: "qc", label: "Au Québec (1-2 semaines)" },
          { value: "canada", label: "Ailleurs au Canada (1-3 semaines)" },
          { value: "etranger", label: "À l'étranger (0.5-6 mois)" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "oui" ||
          answers["step_signification"]?.contacts_recents === "oui",
      },
      {
        id: "pays_etranger",
        label: "Choisissez le pays (si à l'étranger)",
        type: "select",
        options: allCountries.map((c) => ({
          value: c.code,
          label: `${c.name} ${
            c.isSignatory ? "(La Haye)" : "(Hors Convention)"
          }`,
        })),
        showIf: (answers) =>
          answers["step_signification"]?.localisation === "etranger",
      },
      {
        id: "contacts_electroniques",
        label: "Avez-vous un contact par e-mail/réseaux sociaux récent (<3 mois) ?",
        options: [
          {
            value: "oui",
            label: "Oui - Mode spécial (nécessite autorisation du tribunal)",
          },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "non" &&
          answers["step_signification"]?.contacts_recents === "non",
      },
      {
        id: "residence_canada",
        label: "La personne a-t-elle déjà résidé au Canada ?",
        options: [
          {
            value: "oui",
            label: "Oui - publication dans un journal local possible",
          },
          { value: "non", label: "Non - estimation incertaine" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "non" &&
          answers["step_signification"]?.contacts_recents === "non" &&
          answers["step_signification"]?.contacts_electroniques === "non",
      },
      {
        id: "mode_special_decision",
        label: "Décision du juge sur le mode spécial",
        options: [
          {
            value: "accepte",
            label: "Accepté (1-2 semaines après autorisation)",
          },
          { value: "refuse", label: "Refusé (blocage possible)" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.contacts_electroniques === "oui" ||
          answers["step_signification"]?.residence_canada === "oui",
      },
    ],
  },

  // 3) Réponse / défense
  {
    id: "step_reponse",
    phase: "judiciaire",
    title: "Réponse / Défense de l'autre parent",
    minDuration: 0.5,
    maxDuration: 1,
    shortContent:
      "Selon la localisation, l'autre parent dispose de 15 ou 30 jours pour répondre (ou moins en urgence).",
    detailedContent: `
      Une fois la demande signifiée, l'autre parent doit répondre :
      <ul class="list-disc list-inside">
        <li>15 jours s'il est au Québec</li>
        <li>30 jours s'il est hors Québec</li>
        <li>3 à 10 jours si mesures provisoires urgentes</li>
      </ul>
      L'absence de réponse permet un jugement par défaut. 
      En cas d'accord, la procédure s'oriente vers une homologation simplifiée.
    `,
    questions: [
      {
        id: "q_reponse_attendue",
        label: "Pensez-vous que l'autre parent répondra ?",
        options: [
          { value: "non", label: "Non (jugement par défaut possible)" },
          { value: "oui_accord", label: "Oui, avec un accord" },
          { value: "oui_conflit", label: "Oui, il va contester" },
        ],
      },
      {
        id: "q_delai_reponse",
        label: "Délai de réponse applicable",
        options: (answers) => {
          const sig = answers["step_signification"] || {};
          const prov = answers["step_provisoires"] || {};

          // Si mesures provisoires => 3-10
          if (prov.mesures_provisoires === "oui") {
            return [{ value: "3-10", label: "3-10 jours", disabled: true }];
          }
          // Sinon, localisation
          if (sig.localisation === "qc") {
            return [{ value: "15", label: "15 jours (Québec)", disabled: true }];
          }
          if (sig.localisation === "canada") {
            return [
              {
                value: "30",
                label: "30 jours (hors QC mais Canada)",
                disabled: true,
              },
            ];
          }
          if (sig.localisation === "etranger") {
            return [{ value: "30", label: "30+ jours (étranger)", disabled: true }];
          }
          // fallback
          return [{ value: "indetermine", label: "Indéterminé", disabled: true }];
        },
        showIf: (answers) =>
          answers["step_reponse"]?.q_reponse_attendue !== "non",
      },
    ],
    showIf: (answers) => {
      const s = answers["step_signification"] || {};
      // Ne pas afficher si signification impossible
      if (
        s.residence_canada === "non" &&
        s.contacts_electroniques === "non" &&
        s.adresse_exacte === "non" &&
        s.contacts_recents === "non"
      ) {
        return false;
      }
      if (s.mode_special_decision === "refuse") {
        return false;
      }
      return true;
    },
  },

  // 4) Mesures provisoires (ex: urgentes)
  {
    id: "step_provisoires",
    phase: "judiciaire",
    title: "Mesures provisoires",
    minDuration: 0.25,
    maxDuration: 0.5,
    shortContent:
      "Décisions temporaires (garde, pension) en attendant le jugement final.",
    detailedContent: `
      Si la situation l'exige (risque pour l'enfant, besoin financier urgent, etc.), 
      le tribunal peut prononcer des mesures provisoires avant la décision finale. 
      Le délai de réponse de l'autre parent est alors raccourci (3-10 jours).
    `,
    questions: [
      {
        id: "mesures_provisoires",
        label: "Avez-vous besoin de mesures provisoires ?",
        options: [
          { value: "oui", label: "Oui (procédure accélérée)" },
          { value: "non", label: "Non" },
        ],
      },
    ],
  },

  // 5) Consentement
  {
    id: "step_consentement",
    phase: "judiciaire",
    title: "Consentement / Accord mutuel",
    minDuration: 0.25,
    maxDuration: 0.25,
    shortContent:
      "Si accord trouvé, il suffit de le soumettre pour homologation (rapide).",
    detailedContent: `
      Si l'autre parent et vous vous entendez sur les modalités (garde/pension), 
      le tribunal homologuera l'entente, après vérification de l'intérêt de l'enfant. 
      Cela peut se faire sans audience conflictuelle.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_accord",
    questions: [],
  },

  // 6) Procédure contestée
  {
    id: "step6",
    phase: "judiciaire",
    title: "Poursuite en cas de contestation",
    minDuration: 1,
    maxDuration: 6,
    shortContent:
      "Sans accord, la procédure suit son cours (interrogatoires, échanges, etc.).",
    detailedContent: `
      Si aucun accord n'est possible, vous procédez en contestation : 
      interrogatoires, communication de documents, 
      éventuellement expertise psychosociale selon la situation.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [],
  },

  // 7) Remises
  {
    id: "step_remises",
    phase: "judiciaire",
    title: "Remises et délais supplémentaires",
    minDuration: 0,
    maxDuration: 3,
    shortContent: "Les avocats peuvent demander des reports, ajoutant quelques semaines/mois.",
    detailedContent: `
      Les parties peuvent demander un report d'audience pour diverses raisons (agenda, 
      besoin d'expertises, etc.). Chaque remise ajoute potentiellement plusieurs semaines 
      ou mois au calendrier initial.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [
      {
        id: "q_remise",
        label: "Pensez-vous qu'une remise sera demandée ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui (ajout de délais)" },
        ],
      },
    ],
  },

  // 8) Délai avant audience finale
  {
    id: "step_delai_proces",
    phase: "judiciaire",
    title: "Délai avant l'audience",
    minDuration: 12,
    maxDuration: 18,
    shortContent:
      "L'audience peut être fixée 12 à 18 mois plus tard (saturations variables).",
    detailedContent: `
      Dans les dossiers contestés, 
      on peut attendre 12 à 18 mois avant l'audience finale, 
      selon l'engorgement de la Cour.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [],
  },

  // 9) Règlement avant audience
  {
    id: "step_reglement_avant",
    phase: "judiciaire",
    title: "Règlement à l'amiable avant l'audience",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Vous pouvez toujours conclure un accord avant l'audience.",
    detailedContent: `
      Même si la procédure contestée est en cours, un accord peut survenir à tout moment. 
      Dans ce cas, vous le soumettez au juge pour homologation.
      <div class="mt-2 p-2 bg-yellow-50 rounded">
        <p class="text-sm font-semibold">Conseil :</p>
        <p class="text-sm">Si vous arrivez à un accord, vous pouvez refaire le questionnaire 
        en indiquant "Oui, accord" pour obtenir une estimation plus courte.</p>
      </div>
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
    questions: [
      {
        id: "q_reglement_avant",
        label: "Souhaitez-vous finaliser un accord avant l'audience ?",
        options: [
          { value: "oui", label: "Oui - Recalculer procédure accord" },
          { value: "non", label: "Non - Continuer jusqu'au procès" },
        ],
      },
    ],
  },

  // 10) Audience / procès
  {
    id: "step7",
    phase: "judiciaire",
    title: "Audience / Procès",
    minDuration: 2,
    maxDuration: 6,
    shortContent:
      "Le juge entend les parties, puis rend sa décision (immédiate ou après délibéré).",
    detailedContent: `
      Chaque parent présente ses arguments, ses preuves (ex. bulletins de salaire, 
      rapports d'experts, etc.). Le juge peut rendre sa décision sur la garde, 
      la pension et autres mesures le jour même ou après délibération.
    `,
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit" &&
      answers["step_reglement_avant"]?.q_reglement_avant === "non",
    questions: [],
  },

  // 11) Jugement par défaut
  {
    id: "step_default_judgement",
    phase: "judiciaire",
    title: "Jugement par défaut",
    minDuration: 1,
    maxDuration: 2,
    shortContent: "Si l'autre parent ne répond pas du tout, vous obtenez un jugement par défaut.",
    detailedContent: `
      Après expiration du délai de réponse, si l'autre parent ne se manifeste pas, 
      vous pouvez demander au tribunal de prononcer un jugement par défaut. 
      Le juge vérifiera vos demandes et rendra sa décision unilatéralement.
    `,
    showIf: (answers) => answers["step_reponse"]?.q_reponse_attendue === "non",
    questions: [],
  },

  // 12) Jugement final
  {
    id: "step8",
    phase: "judiciaire",
    title: "Jugement final & conclusion",
    minDuration: 1,
    maxDuration: 7,
    shortContent:
      "Obtention du jugement final et ses effets : garde, pension, etc. (jusqu'à 6 mois+).",
    detailedContent: `
      Une fois l'audience terminée ou l'accord homologué, 
      il peut s'écouler jusqu'à 6 mois (parfois plus) pour l'émission officielle 
      du jugement écrit. Ce jugement établit la garde et la pension de façon exécutoire.
    `,
    questions: [],
  },
];

// ────────────────────────────────────────────────────────────────────────────
// 5) PHASE 3 : Post-séparation
// ────────────────────────────────────────────────────────────────────────────

const postDecisionSteps = [
  {
    id: "application_jugement",
    phase: "postDecision",
    title: "Application du jugement",
    shortContent: "Mise en place des modalités de garde et pension.",
    detailedContent: `
      Une fois le jugement rendu, il faut l'appliquer concrètement :
      - Organiser les transitions entre parents
      - Mettre en place le calendrier de garde
      - Établir le système de paiement de pension
    `,
    minDuration: 0.5,
    maxDuration: 1,
    questions: [
      {
        id: "modalites_comprises",
        label: "Les modalités du jugement sont-elles claires ?",
        options: [
          { value: "oui", label: "Oui, tout est clair" },
          { value: "non", label: "Non, j'ai besoin d'éclaircissements" },
        ],
      },
    ],
  },
  {
    id: "modifications",
    phase: "postDecision",
    title: "Modifications ultérieures",
    shortContent:
      "Demande de modification en cas de changement majeur (revenus, déménagement, etc.).",
    detailedContent: `
      Les ordonnances sur la garde ou la pension peuvent être révisées 
      en cas de changement significatif (ex. perte d'emploi, déménagement, etc.).
    `,
    minDuration: 0,
    maxDuration: 0,
    questions: [],
  },
  {
    id: "faq_checklist",
    phase: "postDecision",
    title: "FAQ et Checklist",
    shortContent: "Réponses aux questions courantes, liste de vérification finale.",
    detailedContent: `
      Assurez-vous d'avoir complété toutes les démarches (documents, 
      attestations, etc.). Consultez la FAQ pour clarifier les points fréquents 
      et finaliser votre dossier.
    `,
    minDuration: 0,
    maxDuration: 0,
    questions: [],
  },
];

// ────────────────────────────────────────────────────────────────────────────
// 6) ASSEMBLAGE : getAllSteps + PHASES_ORDER
// ────────────────────────────────────────────────────────────────────────────

function getAllSteps() {
  return [...preparationSteps, ...judiciaireSteps, ...postDecisionSteps];
}

const PHASES_ORDER = [
  { id: "preparation", label: "Phase 1 : Préparation" },
  { id: "judiciaire", label: "Phase 2 : Processus judiciaire" },
  { id: "postDecision", label: "Phase 3 : Post-décision" }
];

// ────────────────────────────────────────────────────────────────────────────
// 7) CALCUL DE LA TIMELINE (avec la logique avancée pour la signification, etc.)
// ────────────────────────────────────────────────────────────────────────────

function calculateTimeline(answers, completedSteps) {
  // We'll track each phase's steps for final display
  const phaseHistory = {
    preparation: [],
    judiciaire: [],
    postDecision: [],
  };

  let totalMin = 0;
  let totalMax = 0;

  // Filter steps that are actually visible given showIf conditions
  const visibleSteps = getAllSteps().filter((step) => {
    if (typeof step.showIf === "function" && step.showIf(answers) === false) {
      return false;
    }
    return true;
  });

  // Helper to compute each step's min/max
  function computeStepDelay(step) {
    // If user has NOT completed that step => zero for now
    if (!completedSteps.includes(step.id)) {
      return { min: 0, max: 0 };
    }

    // Special handling for document preparation
    if (step.id === "documents") {
      const userVal = parseMonths(answers["documents"]?.q_docs_delay);
      if (userVal > 0) {
        phaseHistory.preparation.push(
          `[${step.title}] Délai personnalisé: ${userVal} mois`
        );
        return { min: userVal, max: userVal };
      }
      phaseHistory.preparation.push(
        `[${step.title}] Délai standard: ${DEFAULT_DOCS_MIN}-${DEFAULT_DOCS_MAX} mois`
      );
      return { min: DEFAULT_DOCS_MIN, max: DEFAULT_DOCS_MAX };
    }

    // Special handling for mediation
    if (step.id === "mediation_familiale") {
      const isStarted = answers["mediation_familiale"]?.q_mediation === "oui";
      if (isStarted) {
        phaseHistory.preparation.push("[Médiation] Déjà en cours: 0.5-1 mois restant");
        return { min: 0.5, max: 1 };
      }
      phaseHistory.preparation.push("[Médiation] À commencer: 1-2 mois");
      return { min: 1, max: 2 };
    }

    // Default handling
    phaseHistory[step.phase].push(
      `[${step.title}] Durée: ${step.minDuration}-${step.maxDuration} mois`
    );
    return { min: step.minDuration, max: step.maxDuration };
  }

  // Process each step in order
  for (const step of visibleSteps) {
    const { min, max } = computeStepDelay(step);
    totalMin += min;
    totalMax += max;
  }

  return {
    totalMin,
    totalMax,
    phaseHistory,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// 8) COMPOSANT PRINCIPAL : GardeTimeline
// ────────────────────────────────────────────────────────────────────────────

export default function GardeTimeline() {
  const { t, i18n } = useTranslation(['timeline', 'common']);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [openStep, setOpenStep] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timelineSummary, setTimelineSummary] = useState({
    totalMin: 0,
    totalMax: 0,
    phaseHistory: {
      preparation: [],
      judiciaire: [],
      postDecision: [],
    },
  });

  // Force component update when language changes
  useEffect(() => {
    // This empty dependency array with i18n.language will trigger a re-render when language changes
    // Adding a console log to verify language changes are detected
    console.log("Language changed to:", i18n.language);
    
    // Force re-render by updating a state value
    setTimelineSummary(prev => ({...prev}));
  }, [i18n.language]);

  // Recalculate timeline whenever answers or completedSteps change
  useEffect(() => {
    setTimelineSummary(calculateTimeline(answers, completedSteps));

    // Auto-set q_delai_reponse based on user data
    const stepSignif = answers["step_signification"] || {};
    if (answers["step_provisoires"]?.mesures_provisoires === "oui") {
      setAnswers((prev) => ({
        ...prev,
        step_reponse: { ...prev.step_reponse, q_delai_reponse: "3-10" },
      }));
    } else if (stepSignif.adresse_exacte === "oui" || stepSignif.contacts_recents === "oui") {
      if (stepSignif.localisation === "qc") {
        setAnswers((prev) => ({
          ...prev,
          step_reponse: { ...prev.step_reponse, q_delai_reponse: "15" },
        }));
      } else if (stepSignif.localisation === "canada" || stepSignif.localisation === "etranger") {
        setAnswers((prev) => ({
          ...prev,
          step_reponse: { ...prev.step_reponse, q_delai_reponse: "30" },
        }));
      }
    }
  }, [answers, completedSteps]);

  /**
   * Handle answer changes for a specific step and question
   * @param {string} stepId - The step identifier
   * @param {string} questionId - The question identifier
   * @param {any} value - The answer value
   */
  function handleAnswerChange(stepId, questionId, value) {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [questionId]: value,
      },
    }));
  }

  /**
   * Check if all questions for a step are answered (if they're visible)
   * @param {object} step
   * @returns {boolean}
   */
  function areAllQuestionsAnswered(step) {
    if (!step.questions?.length) return true;
    const stepAns = answers[step.id] || {};
    return step.questions.every((q) => {
      if (typeof q.showIf === "function" && !q.showIf(answers)) return true;
      // Allow empty for certain fields (e.g., doc delay)
      if (["q_docs_delay", "redaction_delay"].includes(q.id)) return true;
      return !!stepAns[q.id];
    });
  }

  /**
   * Mark a step as "done" (completed), if all required questions are answered
   * Also ensures previous steps are done in order.
   * @param {object} step
   */
  function markStepAsDone(step) {
    if (!areAllQuestionsAnswered(step)) {
      alert(t('garde:gardeGuide.alerts.completeRequired'));
      return;
    }
    
    // Ensure previous steps are completed
    const visibleSteps = getAllSteps().filter(
      (s) => !s.showIf || s.showIf(answers) !== false
    );
    const currentIndex = visibleSteps.findIndex((s) => s.id === step.id);
    const prevNotDone = visibleSteps
      .slice(0, currentIndex)
      .some((s) => !completedSteps.includes(s.id));
      
    if (prevNotDone) {
      alert(t('garde:gardeGuide.alerts.completePrevious', { step: step.title }));
      return;
    }

    if (window.confirm(t('garde:gardeGuide.alerts.confirmStep', { step: step.title }))) {
      setCompletedSteps((prev) => (prev.includes(step.id) ? prev : [...prev, step.id]));
      setOpenStep(null);

      // Si c'est l'étape "step_reglement_avant" et que l'utilisateur choisit "oui"
      if (
        step.id === "step_reglement_avant" &&
        answers["step_reglement_avant"]?.q_reglement_avant === "oui"
      ) {
        if (
          window.confirm(
            "Vous souhaitez un règlement amiable avant l'audience. Voulez-vous réinitialiser pour recalculer un parcours accord ?"
          )
        ) {
          resetAll();
        }
      }
    }
  }

  /**
   * Reset all data and start over
   */
  function resetAll() {
    if (window.confirm(t('garde:gardeGuide.actions.resetConfirm'))) {
      setCompletedSteps([]);
      setOpenStep(null);
      setAnswers({});
      setTimelineSummary({
        totalMin: 0,
        totalMax: 0,
        phaseHistory: {
          preparation: [],
          judiciaire: [],
          postDecision: [],
        },
      });
    }
  }

  /**
   * For the "progress bar" target: sum up the minimal durations
   * @param {object} step
   * @returns {number}
   */
  function getPlannedStepDelay(step) {
    if (step.id === "documents") {
      const val = parseMonths(answers.documents?.q_docs_delay);
      return val > 0 ? val : DEFAULT_DOCS_MIN;
    }
    if (step.id === "step_depot_demande") {
      const val = parseMonths(answers["step_depot_demande"]?.redaction_delay);
      return val > 0 ? val : DEFAULT_DOCS_MAX;
    }
    // signification, etc. => on peut se contenter du minDuration comme base
    return step.minDuration;
  }

  // Regroupement par phase
  const groupedPhases = PHASES_ORDER.map((ph) => {
    const stepsInPhase = getAllSteps().filter(
      (s) => s.phase === ph.id && (!s.showIf || s.showIf(answers) !== false)
    );
    return { ...ph, steps: stepsInPhase };
  });

  // Filter visible steps
  const visibleSteps = getAllSteps().filter(
    (s) => !s.showIf || s.showIf(answers) !== false
  );

  // Calcul "progression" (plannedTotal) simplifié
  let plannedTotal = visibleSteps.reduce((acc, s) => acc + getPlannedStepDelay(s), 0);
  
  const { totalMin, totalMax, phaseHistory } = timelineSummary;
  const progressPercent =
    plannedTotal > 0 ? Math.min(Math.round((totalMin / plannedTotal) * 100), 100) : 0;

  return (
    <>
      <DashNavbar />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
          <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
            <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
            {t('garde:gardeGuide.timeline.title')}
            <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
          </h1>
          <p className="text-center text-blue-600 max-w-3xl mx-auto">
            {t('garde:gardeGuide.timeline.subtitle')}
          </p>
        </div>

        {/* Top 3 cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8 px-4">
          {/* Card 1: Progression */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
              {t('garde:gardeGuide.interface.cards.progress.title')}
            </h3>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">{progressPercent}% {t('garde:gardeGuide.interface.cards.progress.completed')}</span>
                <span className="text-slate-500 text-xs">
                  {totalMin.toFixed(1)} / {plannedTotal.toFixed(1)} {t('garde:gardeGuide.interface.cards.progress.months')}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Card 2: Estimation */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">⏱️</span>
              {t('garde:gardeGuide.interface.cards.estimation.title')}
            </h3>
            <div className="flex items-center justify-center h-16">
              {totalMin === 0 && totalMax === 0 ? (
                <p className="text-slate-500 text-center text-sm">
                  {t('garde:gardeGuide.interface.cards.estimation.completeStepsPrompt')}
                </p>
              ) : (
                <div className="text-center">
                  <span className="text-2xl font-bold text-blue-700">
                    {totalMin} {t('garde:gardeGuide.interface.cards.estimation.to')} {totalMax} {t('garde:gardeGuide.interface.cards.estimation.months')}
                  </span>
                  <p className="text-xs text-slate-500 mt-1">{t('garde:gardeGuide.interface.cards.estimation.basedOnAnswers')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Actions */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">🔄</span>
              {t('garde:gardeGuide.interface.cards.actions.title')}
            </h3>
            <div className="flex justify-center items-center h-16">
              <button
                onClick={resetAll}
                className="btn btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
              >
                {t('garde:gardeGuide.interface.cards.actions.reset')}
              </button>
            </div>
          </div>
        </div>

        {/* Phase History / Detailed calculation */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8 mx-4">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">📋</span>
            {t('garde:gardeGuide.interface.details.title')}
          </h3>
          {Object.keys(phaseHistory).some((phase) => phaseHistory[phase].length > 0) ? (
            <div className="space-y-4">
              {Object.keys(phaseHistory).map((phase) => {
                const msgs = phaseHistory[phase];
                if (!msgs.length) return null;

                return (
                  <div key={phase} className={`p-3 rounded-lg ${
                    phase === "preparation"
                      ? "bg-blue-100 border-blue-200 text-blue-700"
                      : phase === "judiciaire"
                      ? "bg-amber-100 border-amber-200 text-amber-700"
                      : "bg-green-100 border-green-200 text-green-700"
                  }`}>
                    <h4 className="font-semibold mb-2 flex items-center">
                      <span className="mr-2">
                        {phase === "preparation" ? "📋" : phase === "judiciaire" ? "⚖️" : "📝"}
                      </span>
                      {t(`garde:gardeGuide.phases.${phase === "preparation" ? "preparation" : phase === "judiciaire" ? "judicial" : "postDecision"}.title`)}
                    </h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {msgs.map((m, idx) => (
                        <li key={idx} className="text-slate-700">
                          {m}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center p-4 text-slate-500">
              <p>{t('garde:gardeGuide.interface.details.noData')}</p>
            </div>
          )}
        </div>

        {/* Steps by phase */}
        <div className="space-y-6 px-4 mb-8">
          {PHASES_ORDER.map((phaseObj) => {
            const stepsInPhase = visibleSteps.filter((s) => s.phase === phaseObj.id);
            if (!stepsInPhase.length) return null;

            return (
              <div key={phaseObj.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Phase header */}
                <div className={`p-4 ${
                  phaseObj.id === "preparation"
                    ? "bg-blue-100 text-blue-700"
                    : phaseObj.id === "judiciaire"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-green-100 text-green-700"
                }`}>
                  <h2 className="text-lg font-bold flex items-center">
                    <span className="mr-3 text-2xl">
                      {phaseObj.id === "preparation" ? "📋" : phaseObj.id === "judiciaire" ? "⚖️" : "📝"}
                    </span>
                    {t(`garde:gardeGuide.phases.${phaseObj.id === "preparation" ? "preparation" : phaseObj.id === "judiciaire" ? "judicial" : "postDecision"}.title`)}
                  </h2>
                </div>

                {/* Steps inside this phase */}
                <div className="p-4 relative">
                  <div className="absolute w-[4px] h-full left-6 top-0 rounded-full z-0 bg-gradient-to-b from-blue-100 via-amber-100 to-green-100"></div>

                  <div className="space-y-8">
                    {stepsInPhase.map((step, idx) => {
                      const globalIndex = getAllSteps().findIndex((x) => x.id === step.id);
                      const isDone = completedSteps.includes(step.id);
                      const isOpen = openStep === step.id;
                      const isActive = isOpen && !isDone;

                      let style = {
                        bg: "bg-white",
                        border: "border-slate-200",
                        text: "text-slate-700",
                        bulletBg: "bg-white",
                        bulletBorder: "border-slate-300",
                        bulletText: "text-slate-700",
                      };

                      if (isDone) {
                        style = {
                          bg: "bg-green-50",
                          border: "border-green-200",
                          text: "text-green-700",
                          bulletBg: "bg-green-500",
                          bulletBorder: "border-green-500",
                          bulletText: "text-white",
                        };
                      } else if (isActive) {
                        style = {
                          bg: "bg-amber-50",
                          border: "border-amber-200",
                          text: "text-amber-700",
                          bulletBg: "bg-amber-50",
                          bulletBorder: "border-amber-500",
                          bulletText: "text-amber-700",
                        };
                      }

                      return (
                        <div key={step.id} className="flex flex-col md:flex-row relative z-10">
                          <div className="mr-6">
                            <div
                              className={`w-12 h-12 rounded-full border-4 shadow-sm flex items-center justify-center text-lg font-bold 
                                ${style.bulletBg} ${style.bulletBorder} ${style.bulletText}
                                transition-all duration-300 ease-in-out
                                ${isDone ? "scale-110" : ""}
                              `}
                              style={{ marginLeft: "-4px" }}
                            >
                              {isDone ? "✓" : globalIndex + 1}
                            </div>
                          </div>

                          <div className="flex-1 pt-1">
                            <div
                              className={`card w-full border ${style.border} ${style.bg} rounded-xl shadow-sm
                              hover:shadow-md transition-all duration-200 ease-in-out
                              ${isActive ? "transform scale-[1.02]" : ""}
                              `}
                            >
                              <div className="card-body p-5">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                  <h3 className={`card-title text-lg ${style.text}`}>
                                    {step.title}
                                  </h3>
                                </div>
                                <p className="mt-3 text-slate-600">
                                  {step.shortContent}
                                </p>

                                {/* Buttons */}
                                <div className="mt-4 flex gap-3 flex-wrap">
                                  <button
                                    className={`btn btn-sm rounded-full px-4 ${
                                      isOpen
                                        ? "bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200"
                                        : "bg-blue-100 border-blue-200 text-blue-700 hover:bg-blue-200"
                                    }`}
                                    onClick={() => setOpenStep(isOpen ? null : step.id)}
                                  >
                                    {isOpen ? (
                                      <>
                                        Masquer
                                        <span className="ml-1">▲</span>
                                      </>
                                    ) : (
                                      <>
                                        Détails & Questions
                                        <span className="ml-1">▼</span>
                                      </>
                                    )}
                                  </button>

                                  {!isDone && (
                                    <button
                                      className="btn btn-sm rounded-full px-4 bg-green-100 border-green-200 text-green-700 hover:bg-green-200"
                                      onClick={() => markStepAsDone(step)}
                                    >
                                      ✓ Terminer
                                    </button>
                                  )}

                                  {isDone && (
                                    <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm font-semibold border border-green-100 self-center">
                                      Étape terminée
                                    </span>
                                  )}
                                </div>

                                {/* Accordion content */}
                                {isOpen && (
                                  <div className="mt-5 animate-[fadeIn_0.3s_ease-in-out]">
                                    <style>{`
                                      @keyframes fadeIn {
                                        from { opacity: 0; transform: translateY(-10px); }
                                        to { opacity: 1; transform: translateY(0); }
                                      }
                                    `}</style>

                                    {/* Detailed content */}
                                    <div className="p-4 border border-slate-200 rounded-lg bg-white text-slate-600 mb-4">
                                      <h4 className="font-semibold text-blue-700 mb-2">
                                        Détails de l'étape
                                      </h4>
                                      {typeof step.detailedContent === "string" ? (
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: step.detailedContent,
                                          }}
                                        />
                                      ) : (
                                        step.detailedContent
                                      )}
                                    </div>

                                    {/* Questions */}
                                    {step.questions?.length > 0 && (
                                      <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                                        <h4 className="font-semibold text-amber-700 mb-3 flex items-center">
                                          <span className="mr-2">❓</span>
                                          Questions pour adapter l'estimation
                                        </h4>
                                        <div className="space-y-5">
                                          {step.questions.map((q) => {
                                            if (typeof q.showIf === "function" && !q.showIf(answers)) {
                                              return null;
                                            }
                                            const stepAns = answers[step.id] || {};
                                            const selectedValue = stepAns[q.id] || "";

                                            if (q.type === "text" || q.type === "number") {
                                              return (
                                                <div
                                                  key={q.id}
                                                  className="bg-white p-3 rounded-lg border border-amber-100"
                                                >
                                                  <label className="font-semibold block mb-1 text-blue-700">
                                                    {q.label}
                                                  </label>
                                                  {q.help && (
                                                    <p className="text-xs text-slate-500 mb-3">
                                                      {q.help}
                                                    </p>
                                                  )}
                                                  <input
                                                    type={q.type}
                                                    className="input input-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                    value={selectedValue}
                                                    onChange={(e) =>
                                                      handleAnswerChange(
                                                        step.id,
                                                        q.id,
                                                        e.target.value
                                                      )
                                                    }
                                                    placeholder={q.type === "text" ? "Votre réponse..." : "0"}
                                                  />
                                                </div>
                                              );
                                            }

                                            if (q.type === "select") {
                                              return (
                                                <div
                                                  key={q.id}
                                                  className="bg-white p-3 rounded-lg border border-amber-100"
                                                >
                                                  <label className="font-semibold block mb-1 text-blue-700">
                                                    {q.label}
                                                  </label>
                                                  {q.help && (
                                                    <p className="text-xs text-slate-500 mb-3">
                                                      {q.help}
                                                    </p>
                                                  )}
                                                  <select
                                                    className="select select-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                    value={selectedValue}
                                                    onChange={(e) =>
                                                      handleAnswerChange(
                                                        step.id,
                                                        q.id,
                                                        e.target.value
                                                      )
                                                    }
                                                  >
                                                    <option value="">
                                                      -- Sélectionnez une option --
                                                    </option>
                                                    {q.options?.map((opt) => (
                                                      <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                              );
                                            }

                                            if (q.options && !q.type) {
                                              const ops =
                                                typeof q.options === "function"
                                                  ? q.options(answers)
                                                  : q.options;
                                              return (
                                                <div
                                                  key={q.id}
                                                  className="bg-white p-3 rounded-lg border border-amber-100"
                                                >
                                                  <label className="font-semibold block mb-1 text-blue-700">
                                                    {q.label}
                                                  </label>
                                                  {q.help && (
                                                    <p className="text-xs text-slate-500 mb-3">
                                                      {q.help}
                                                    </p>
                                                  )}
                                                  <div className="space-y-2 mt-3">
                                                    {ops.map((opt) => (
                                                      <label
                                                        key={opt.value}
                                                        className={`flex items-center gap-2 p-2 hover:bg-blue-50 rounded-md transition-colors
                                                          ${
                                                            selectedValue === opt.value
                                                              ? "bg-blue-50 border border-blue-100"
                                                              : ""
                                                          }
                                                          ${
                                                            opt.disabled
                                                              ? "opacity-50 cursor-not-allowed"
                                                              : "cursor-pointer"
                                                          }
                                                        `}
                                                      >
                                                        <input
                                                          type="radio"
                                                          className="radio radio-sm radio-primary"
                                                          name={`${step.id}-${q.id}`}
                                                          value={opt.value}
                                                          checked={selectedValue === opt.value}
                                                          onChange={() => {
                                                            if (!opt.disabled) {
                                                              handleAnswerChange(step.id, q.id, opt.value);
                                                            }
                                                          }}
                                                          disabled={opt.disabled}
                                                        />
                                                        <span>{opt.label}</span>
                                                      </label>
                                                    ))}
                                                  </div>
                                                </div>
                                              );
                                            }

                                            return null;
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legal note */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mb-8 mx-4">
          <p className="italic text-center">
            {t('garde:gardeGuide.legalNote')}
          </p>
        </div>
      </div>
    </>
  );
}
